<template>
  <svg
    :class="iconClasses"
    :style="iconStyles"
    aria-hidden="true"
    v-bind="$attrs"
  >
    <use :xlink:href="symbolId" />
  </svg>
</template>

<script setup lang="ts">
import { computed, type CSSProperties } from 'vue'

/**
 * IconFont 组件属性接口
 */
interface IconFontProps {
  /** 图标名称（不包含前缀） */
  name: string
  /** 图标大小，支持数字（px）或字符串 */
  size?: number | string
  /** 图标颜色 */
  color?: string
  /** 自定义 CSS 类名 */
  className?: string
  /** 图标前缀，默认为 'icon-' */
  prefix?: string
  /** 是否禁用鼠标事件 */
  disabled?: boolean
  /** 旋转角度 */
  rotate?: number
  /** 是否开启加载动画 */
  spin?: boolean
}

const props = withDefaults(defineProps<IconFontProps>(), {
  size: 16,
  prefix: '',
  disabled: false,
  rotate: 0,
  spin: false
})

/**
 * 计算 symbol ID
 */
const symbolId = computed(() => `#${props.prefix}${props.name}`)

/**
 * 计算图标样式类
 */
const iconClasses = computed(() => [
  'iconfont',
  {
    'iconfont--disabled': props.disabled,
    'iconfont--spin': props.spin
  },
  props.className
])

/**
 * 计算图标内联样式
 */
const iconStyles = computed((): CSSProperties => {
  const styles: CSSProperties = {}

  // 处理尺寸
  if (props.size) {
    const sizeValue = typeof props.size === 'number' ? `${props.size}px` : props.size
    styles.width = sizeValue
    styles.height = sizeValue
    styles.fontSize = sizeValue
  }

  // 处理颜色
  if (props.color) {
    styles.color = props.color
    styles.fill = 'currentColor'
  }

  // 处理旋转
  if (props.rotate && props.rotate !== 0) {
    styles.transform = `rotate(${props.rotate}deg)`
  }

  return styles
})

// 暴露组件名称供调试使用
defineOptions({
  name: 'IconFont',
  inheritAttrs: false
})
</script>

<style lang="less" scoped>
.iconfont {
  display: inline-block;
  width: 1em;
  height: 1em;
  fill: currentColor;
  vertical-align: -0.15em;
  font-size: inherit;
  overflow: hidden;
  transition: all 0.3s ease;

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  &--spin {
    animation: icon-spin 1s linear infinite;
  }
}

@keyframes icon-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style> 